/**
 * Auth form primitives — inputs with inline validation, password
 * strength meter, checkbox, submit button, error banner.
 *
 * Ported from the Claude Design handoff (auth/fields.jsx) so all
 * four auth pages share the exact same look and behaviour.
 */

import { useState } from 'react'
import { JAI_COLORS, JAI_FONTS } from './JaiBrand'
import { Notice } from './JaiV2'

// Extra tones used by the design that aren't in JAI_COLORS
export const AUTH_TONES = {
  faint: 'rgba(10,31,68,0.40)',
  warn: '#C7841C',
}

// ── Icons (inline, stroked to match brand) ──────────────────
export function Icon({ path, size = 16, stroke = 'currentColor', sw = 1.7, fill = 'none', children }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={stroke}
      strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      {children || <path d={path} />}
    </svg>
  )
}
export const EyeIcon = (p) => <Icon {...p}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></Icon>
export const EyeOffIcon = (p) => <Icon {...p}><path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c6.5 0 10 7 10 7a13.2 13.2 0 0 1-1.67 2.42" /><path d="M6.06 6.06C3.42 7.74 2 12 2 12s3.5 7 10 7a9.7 9.7 0 0 0 4.94-1.34" /><line x1="2" y1="2" x2="22" y2="22" /><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" /></Icon>
export const CheckIcon = (p) => <Icon {...p}><path d="M20 6 9 17l-5-5" /></Icon>
export const AlertIcon = (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><line x1="12" y1="8" x2="12" y2="12.5" /><circle cx="12" cy="16" r="0.6" fill={p.stroke || 'currentColor'} /></Icon>
export const SpinIcon = ({ size = 14, stroke = JAI_COLORS.mute }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ animation: 'jaiSpin 0.7s linear infinite' }}>
    <circle cx="12" cy="12" r="9" stroke={stroke} strokeWidth="2.5" opacity="0.25" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" />
  </svg>
)
export const ArrowLeft = (p) => <Icon {...p}><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></Icon>
export const MailIcon = (p) => <Icon {...p}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></Icon>

// ── Validators (mirror the backend rules in validationService.js) ──
export const validators = {
  firstName: (v) => (!v.trim() ? 'First name is required' : ''),
  lastName: (v) => (!v.trim() ? 'Last name is required' : ''),
  username: (v) => {
    if (!v.trim()) return 'Username is required'
    if (v.length < 3) return 'At least 3 characters'
    if (v.length > 20) return 'Max 20 characters'
    if (!/^[a-zA-Z0-9_]+$/.test(v)) return 'Letters, numbers & underscores only'
    return ''
  },
  email: (v) => {
    if (!v.trim()) return 'Email address is required'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Enter a valid email address'
    return ''
  },
  password: (v) => {
    if (!v) return 'Password is required'
    if (v.length < 8) return 'At least 8 characters'
    if (!/[A-Z]/.test(v)) return 'Add one uppercase letter'
    if (!/[0-9]/.test(v)) return 'Add one number'
    return ''
  },
}

// Password strength scoring → { score 0-4, label, color }
export function scorePassword(v) {
  if (!v) return { score: 0, label: '', pct: 0, color: JAI_COLORS.hair }
  let s = 0
  if (v.length >= 8) s++
  if (v.length >= 12) s++
  if (/[A-Z]/.test(v) && /[a-z]/.test(v)) s++
  if (/[0-9]/.test(v)) s++
  if (/[^A-Za-z0-9]/.test(v)) s++
  s = Math.min(s, 4)
  const map = [
    { label: 'Too weak', color: JAI_COLORS.error },
    { label: 'Weak', color: JAI_COLORS.error },
    { label: 'Fair', color: AUTH_TONES.warn },
    { label: 'Good', color: JAI_COLORS.blue },
    { label: 'Strong', color: JAI_COLORS.success },
  ]
  return { score: s, label: map[s].label, pct: (s / 4) * 100, color: map[s].color }
}

// ── Text field with floating validation row ─────────────────
export function Field({
  label, type = 'text', value, onChange, onBlur, placeholder, autoFocus,
  error, valid, hint, right, status, leftIcon, autoComplete, name,
}) {
  const [focused, setFocused] = useState(false)
  const showError = !!error
  const showValid = !showError && valid
  let borderColor = JAI_COLORS.hair
  if (focused) borderColor = JAI_COLORS.blue
  if (showError) borderColor = JAI_COLORS.error
  else if (showValid && !focused) borderColor = 'rgba(26,135,84,0.45)'

  return (
    <div>
      {label && (
        <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: JAI_COLORS.navy, marginBottom: 7, fontFamily: JAI_FONTS.tight, letterSpacing: '-0.01em' }}>
          {label}
        </label>
      )}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        {leftIcon && (
          <span style={{ position: 'absolute', left: 12, color: focused ? JAI_COLORS.blue : AUTH_TONES.faint, display: 'flex', transition: 'color 0.18s' }}>
            {leftIcon}
          </span>
        )}
        <input
          type={type}
          name={name}
          value={value}
          autoComplete={autoComplete}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); onBlur && onBlur() }}
          autoFocus={autoFocus}
          placeholder={placeholder}
          style={{
            width: '100%',
            background: focused ? JAI_COLORS.white : 'rgba(255,255,255,0.7)',
            border: `1px solid ${borderColor}`,
            color: JAI_COLORS.navy,
            borderRadius: 10,
            padding: '11px 12px',
            paddingLeft: leftIcon ? 38 : 12,
            paddingRight: (right || showValid || status) ? 40 : 12,
            // 16px, not 14 — under 16px, WKWebView (and mobile Safari) zooms
            // the whole page in on focus to make the field readable, which
            // is what made this look like a layout overflow bug. Same rule
            // the overnight run applied everywhere else (Phase 2.4); this
            // shared field component was the one place it got missed.
            fontSize: 16,
            fontFamily: JAI_FONTS.tight,
            outline: 'none',
            transition: 'all 0.18s',
            boxShadow: focused ? `0 0 0 3px ${showError ? 'rgba(214,58,58,0.13)' : 'rgba(30,91,214,0.13)'}` : 'none',
          }}
        />
        <span style={{ position: 'absolute', right: 12, display: 'flex', alignItems: 'center' }}>
          {status === 'checking' && <SpinIcon />}
          {status === 'available' && <CheckIcon size={16} stroke={JAI_COLORS.success} sw={2.4} />}
          {!status && showValid && <CheckIcon size={16} stroke={JAI_COLORS.success} sw={2.4} />}
          {right}
        </span>
      </div>
      <div style={{ minHeight: 18, marginTop: 5, display: 'flex', alignItems: 'center', gap: 5 }}>
        {showError ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: JAI_COLORS.error, fontSize: 11.5, fontFamily: JAI_FONTS.tight, animation: 'jaiShake 0.32s' }}>
            <AlertIcon size={13} stroke={JAI_COLORS.error} /> {error}
          </span>
        ) : status === 'taken' ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: JAI_COLORS.error, fontSize: 11.5, fontFamily: JAI_FONTS.tight }}>
            <AlertIcon size={13} stroke={JAI_COLORS.error} /> That username is taken
          </span>
        ) : status === 'available' ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: JAI_COLORS.success, fontSize: 11.5, fontFamily: JAI_FONTS.tight }}>
            <CheckIcon size={13} stroke={JAI_COLORS.success} sw={2.4} /> Username available
          </span>
        ) : hint ? (
          <span style={{ color: AUTH_TONES.faint, fontSize: 11.5, fontFamily: JAI_FONTS.tight }}>{hint}</span>
        ) : null}
      </div>
    </div>
  )
}

// ── Password field (toggle + optional strength meter) ───────
export function PasswordField({ label, value, onChange, onBlur, error, valid, placeholder, autoComplete, showStrength, autoFocus, name }) {
  const [show, setShow] = useState(false)
  const st = scorePassword(value)
  return (
    <div>
      <Field
        label={label}
        type={show ? 'text' : 'password'}
        value={value}
        name={name}
        onChange={onChange}
        onBlur={onBlur}
        error={error}
        valid={valid && !showStrength}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        placeholder={placeholder}
        right={
          <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? 'Hide password' : 'Show password'}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: AUTH_TONES.faint, display: 'flex', padding: 2 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = JAI_COLORS.navy)}
            onMouseLeave={(e) => (e.currentTarget.style.color = AUTH_TONES.faint)}>
            {show ? <EyeOffIcon size={17} /> : <EyeIcon size={17} />}
          </button>
        }
      />
      {showStrength && value && (
        <div style={{ marginTop: -8, marginBottom: 2 }}>
          <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} style={{
                height: 4, flex: 1, borderRadius: 2,
                background: i < st.score ? st.color : JAI_COLORS.hair,
                transition: 'background 0.3s',
              }} />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontFamily: JAI_FONTS.tight }}>
            <span style={{ color: AUTH_TONES.faint }}>Password strength</span>
            <span style={{ color: st.color, fontWeight: 600 }}>{st.label}</span>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Checkbox ────────────────────────────────────────────────
export function Checkbox({ checked, onChange, children }) {
  return (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, cursor: 'pointer', userSelect: 'none' }}>
      <span style={{
        width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1,
        border: `1.5px solid ${checked ? JAI_COLORS.blue : JAI_COLORS.hair}`,
        background: checked ? JAI_COLORS.blue : JAI_COLORS.white,
        display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
      }}>
        {checked && <CheckIcon size={12} stroke="white" sw={3} />}
      </span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
      <span style={{ fontSize: 12.5, color: JAI_COLORS.mute, fontFamily: JAI_FONTS.tight, lineHeight: 1.5 }}>{children}</span>
    </label>
  )
}

// ── Primary submit button ───────────────────────────────────
export function PrimaryButton({ children, loading, disabled, onClick, type = 'submit' }) {
  const off = loading || disabled
  return (
    <button
      type={type}
      disabled={off}
      onClick={onClick}
      style={{
        width: '100%',
        background: off ? 'rgba(30,91,214,0.45)' : `linear-gradient(135deg, ${JAI_COLORS.blueBright} 0%, ${JAI_COLORS.blue} 55%, #1A4FBF 100%)`,
        color: 'white', fontWeight: 650, borderRadius: 10, padding: '12px 16px', fontSize: 14,
        border: 'none', cursor: off ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
        fontFamily: JAI_FONTS.tight, letterSpacing: '-0.01em',
        boxShadow: off ? 'none' : '0 6px 18px rgba(30,91,214,0.32)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      }}
      onMouseEnter={(e) => { if (!off) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 9px 24px rgba(30,91,214,0.40)' } }}
      onMouseLeave={(e) => { if (!off) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(30,91,214,0.32)' } }}
    >
      {loading && <SpinIcon size={15} stroke="rgba(255,255,255,0.85)" />}
      {children}
    </button>
  )
}

// ── Form-level error banner ─────────────────────────────────
export function ErrorBanner({ children }) {
  if (!children) return null
  return (
    <Notice tone="error" style={{ marginTop: 0, marginBottom: 18, animation: 'jaiShake 0.32s', fontFamily: JAI_FONTS.tight }}>{children}</Notice>
  )
}

// ── Shared text bits ────────────────────────────────────────
export function FormHeader({ title, sub }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <h2 style={{ fontSize: 22, fontWeight: 750, color: JAI_COLORS.navy, margin: 0, fontFamily: JAI_FONTS.tight, letterSpacing: '-0.025em' }}>{title}</h2>
      {sub && <p style={{ fontSize: 13.5, color: JAI_COLORS.mute, margin: '7px 0 0', fontFamily: JAI_FONTS.tight, lineHeight: 1.5 }}>{sub}</p>}
    </div>
  )
}

export function TextLink({ children, onClick, size = 13 }) {
  return (
    <button type="button" onClick={onClick}
      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: JAI_COLORS.blue, fontWeight: 600, fontSize: size, fontFamily: JAI_FONTS.tight }}
      onMouseEnter={(e) => (e.currentTarget.style.color = '#163F9E')}
      onMouseLeave={(e) => (e.currentTarget.style.color = JAI_COLORS.blue)}>
      {children}
    </button>
  )
}

export function FootSwitch({ prompt, action, onClick }) {
  return (
    <p style={{ textAlign: 'center', fontSize: 13, color: JAI_COLORS.mute, marginTop: 22, fontFamily: JAI_FONTS.tight }}>
      {prompt}{' '}<TextLink onClick={onClick}>{action}</TextLink>
    </p>
  )
}

// ── Success badge circle ────────────────────────────────────
export function SuccessBadge({ icon, tint = 'blue' }) {
  const ring = tint === 'success' ? '#1A8754' : '#1E5BD6'
  return (
    <div style={{ display: 'flex', marginBottom: 18 }}>
      <div style={{ width: 56, height: 56, borderRadius: 16, border: `2px solid ${ring}`, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'jaiPop 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}>
        {icon}
      </div>
    </div>
  )
}
